import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { History, Code, FileCode, MessageSquare, ExternalLink, Pencil, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CodeSnippet } from '@/components/chat/ChatDialogs';
import { useIsMobile } from '@/hooks/use-mobile';

// Utility to strip code block markdown (```lang ... ```)
function stripCodeBlockMarkdown(code: string): string {
  if (!code) return '';
  // Remove leading ```lang or ```
  code = code.replace(/^```[a-zA-Z0-9]*\s*/, '');
  // Remove trailing ```
  code = code.replace(/```$/, '');
  // Remove any trailing whitespace
  return code.trim();
}

interface CodeHistoryProps {
  codeSnippets: CodeSnippet[];
  onSelectSnippet: (snippetId: string) => void;
  refreshCodeSnippets: () => Promise<void>;
  updateCodeSnippetName: (snippetId: string, newName: string) => Promise<boolean>;
}

const CodeHistory: React.FC<CodeHistoryProps> = ({ codeSnippets, onSelectSnippet, refreshCodeSnippets, updateCodeSnippetName }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingId, setSavingId] = useState<string | null>(null);

  if (!codeSnippets || codeSnippets.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground h-full flex flex-col items-center justify-center border-l">
        <History className="mx-auto mb-2 h-8 w-8 opacity-50" />
        <p>No code snippets in history</p>
        <p className="text-xs mt-2">Save code to see it here</p>
      </div>
    );
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const getLanguageIcon = (language: string) => {
    return <Code size={16} className="text-primary" />;
  };

  const handleGoToChat = (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/chat/${chatId}`);
  };

  // Start editing a snippet name
  const startEditing = (snippetId: string, currentName: string) => {
    setEditingId(snippetId);
    setEditValue(currentName);
  };

  // Save the new name and refresh the list
  const saveEdit = async (snippetId: string) => {
    if (!editValue.trim()) return;
    setSavingId(snippetId);
    const success = await updateCodeSnippetName(snippetId, editValue.trim());
    setSavingId(null);
    if (success) {
      setEditingId(null);
      await refreshCodeSnippets();
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  return (
    <div className="flex flex-col h-full bg-background border-l">
      <div className="p-3 border-b flex items-center gap-2 bg-primary/10">
        <FileCode size={16} className="text-primary" />
        <h3 className="text-sm font-bold">Code History ({codeSnippets.length})</h3>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="space-y-1 p-2">
          {codeSnippets.map((snippet) => (
            <div key={snippet.id} className="relative group">
              <Button
                variant="ghost"
                className="flex flex-col items-start w-full h-auto py-2 px-3 text-left hover:bg-primary/5 transition-colors"
                onClick={() => onSelectSnippet(snippet.id)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {getLanguageIcon(snippet.language)}
                    {editingId === snippet.id ? (
                      <form
                        onSubmit={e => {
                          e.preventDefault();
                          saveEdit(snippet.id);
                        }}
                        className="flex items-center gap-1"
                      >
                        <input
                          className="text-sm font-medium border rounded px-1 py-0.5 w-32 bg-background text-foreground border-border focus:outline-none focus:ring-2 focus:ring-primary"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          autoFocus
                          disabled={savingId === snippet.id}
                          onKeyDown={e => {
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <Button
                          type="submit"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          disabled={savingId === snippet.id}
                        >
                          <Check size={14} />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={cancelEdit}
                          disabled={savingId === snippet.id}
                        >
                          <X size={14} />
                        </Button>
                      </form>
                    ) : (
                      <>
                        <span className={`font-medium text-sm ${isMobile ? "max-w-[100px] truncate" : ""}`}>
                          {snippet.name || 'Untitled'}
                        </span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 p-0 ml-1 opacity-60 hover:opacity-100"
                          onClick={e => {
                            e.stopPropagation();
                            startEditing(snippet.id, snippet.name || '');
                          }}
                          tabIndex={-1}
                          title="Rename snippet"
                        >
                          <Pencil size={13} />
                        </Button>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(snippet.timestamp)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate font-mono w-full">
                  {stripCodeBlockMarkdown(snippet.content).substring(0, isMobile ? 30 : 40)}...
                </div>
              </Button>
              {snippet.chatId && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-6 w-6 p-0"
                  onClick={(e) => handleGoToChat(snippet.chatId, e)}
                  title="Go to chat"
                >
                  {isMobile ? <ExternalLink size={14} /> : <MessageSquare size={14} />}
                </Button>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default CodeHistory;